import React, { useState, useEffect } from 'react';
import { 
  Layout, 
  Typography, 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Progress,
  Table,
  Tag,
  Space,
  message,
  Button
} from 'antd';
import { 
  UserOutlined, 
  CheckCircleOutlined, 
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  PhoneOutlined,
  IdcardOutlined,
  ReloadOutlined,
  RiseOutlined,
  DatabaseOutlined,
  LinkOutlined,
  SettingOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { authService } from '../services/auth';
import { AIThreatWidget } from '../components/AIThreatWidget';
import { AIStatusWidget } from '../components/AIStatusWidget';
import { AIRealTimeMonitor } from '../components/AIRealTimeMonitor';
import { AILearningProgress } from '../components/AILearningProgress';
import { LayoutCustomizer } from '../components/LayoutCustomizer';
import SafetyIntelligenceWidget from '../components/SafetyIntelligenceWidget';
import { layoutPreferencesService, DashboardLayout, WidgetConfig } from '../services/layoutPreferences';

const { Content } = Layout;
const { Title } = Typography;

interface DashboardStats {
  totalUsers: number;
  activeUsers: number;
  pendingVerifications: number;
  recentOtpRequests: number;
  todayRegistrations: number;
  verificationRate: string;
  activeRate: string;
}

interface RecentUser {
  id: string;
  phone: string;
  name?: string;
  surname?: string;
  verification_status: string;
  created_at: string;
}

const DashboardPage: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    activeUsers: 0,
    pendingVerifications: 0,
    recentOtpRequests: 0,
    todayRegistrations: 0,
    verificationRate: '0',
    activeRate: '0'
  });
  const [recentUsers, setRecentUsers] = useState<RecentUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [dbConnected, setDbConnected] = useState<boolean | null>(null);
  
  // Layout Customization State
  const [customizerVisible, setCustomizerVisible] = useState(false);
  const [currentLayout, setCurrentLayout] = useState<DashboardLayout | null>(null);
  const [layoutWidgets, setLayoutWidgets] = useState<WidgetConfig[]>([]);

  // Layout yükleme
  const loadLayout = () => {
    const layout = layoutPreferencesService.getCurrentLayout();
    if (layout) {
      setCurrentLayout(layout);
      setLayoutWidgets(layout.widgets);
    } else {
      // Default layout kullan
      layoutPreferencesService.resetToDefault();
      const defaultLayout = layoutPreferencesService.getCurrentLayout();
      setCurrentLayout(defaultLayout);
      setLayoutWidgets(defaultLayout?.widgets || []);
    }
  };

  // Layout değişikliği handler
  const handleLayoutChange = (layoutId: string) => {
    const layout = layoutPreferencesService.getCurrentLayout();
    if (layout) {
      setCurrentLayout(layout);
      setLayoutWidgets(layout.widgets);
    }
  };

  // Widget görünürlük kontrolü
  const isWidgetVisible = (widgetType: WidgetConfig['type']): boolean => {
    const widget = layoutWidgets.find(w => w.type === widgetType);
    return widget ? widget.visible && !widget.minimized : true;
  };

  // Widget boyutunu al
  const getWidgetSize = (widgetType: WidgetConfig['type']) => {
    const widget = layoutWidgets.find(w => w.type === widgetType);
    if (widget) {
      return {
        xs: 24,
        sm: Math.min(24, widget.position.w * 2),
        lg: widget.position.w
      };
    }
    return { xs: 24, sm: 12, lg: 6 }; // Default
  };

  // Dashboard verilerini getir
  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('gmh_admin_token'); // Correct token key
      
      console.log('🎯 Fetching dashboard data with token:', token ? 'Present' : 'Missing');
      
      // İstatistikleri önce getir (30 saniye timeout)
      try {
        console.log('📊 Fetching dashboard stats...');
        const api = authService.getApiInstance();
        const statsResponse = await api.get('/api/data/dashboard/stats', {
          timeout: 30000 // 30 seconds
        });

        if (statsResponse.data.success) {
          console.log('✅ Dashboard stats loaded:', statsResponse.data.data.stats);
          setStats(statsResponse.data.data.stats);
        }
      } catch (statsError: any) {
        console.error('❌ Stats loading failed:', statsError.message);
        message.warning('İstatistikler yüklenemedi: ' + statsError.message);
      }

      // Sonra kullanıcıları getir (30 saniye timeout)
      try {
        console.log('👥 Fetching recent users...');
        const api = authService.getApiInstance();
        const usersResponse = await api.get('/api/data/users?limit=10', {
          timeout: 30000 // 30 seconds
        });

        if (usersResponse.data.success) {
          console.log('✅ Recent users loaded:', usersResponse.data.data.users.length, 'users');
          setRecentUsers(usersResponse.data.data.users);
        }
      } catch (usersError: any) {
        console.error('❌ Users loading failed:', usersError.message);
        message.warning('Kullanıcılar yüklenemedi: ' + usersError.message);
      }

    } catch (error: any) {
      console.error('❌ Dashboard data loading failed:', error);
      message.error('Dashboard verileri yüklenirken hata oluştu: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Railway bağlantısını test et
  const testRailwayConnection = async () => {
    try {
      const token = localStorage.getItem('gmh_admin_token');
      console.log('🔍 Testing Railway connection...');
      
      const api = authService.getApiInstance();
      const response = await api.get('/api/data/test-connection', {
        timeout: 15000 // 15 seconds
      });

      if (response.data.success) {
        setDbConnected(true);
        message.success('Railway bağlantısı başarılı! 🎉');
        console.log('✅ Railway connection successful');
        // Bağlantı başarılıysa dashboard verilerini yenile
        fetchDashboardData();
      }
    } catch (error: any) {
      setDbConnected(false);
      console.error('❌ Railway connection failed:', error);
      message.error('Railway bağlantısı başarısız: ' + error.message);
    }
  };

  useEffect(() => {
    loadLayout(); // Layout'u yükle
    fetchDashboardData();
    // Her 60 saniyede bir otomatik yenile
    const interval = setInterval(fetchDashboardData, 60000);
    return () => clearInterval(interval);
  }, []);

  // Doğrulama durumu rengi
  const getVerificationColor = (status: string) => {
    switch (status) {
      case 'verified': return 'green';
      case 'pending': return 'orange';
      case 'failed': return 'red';
      default: return 'default';
    }
  };

  const recentUsersColumns: ColumnsType<RecentUser> = [
    {
      title: 'Kullanıcı',
      key: 'user',
      render: (_, record) => (
        <Space>
          <UserOutlined />
          <div>
            <div style={{ fontWeight: 'bold' }}>
              {record.name || record.surname 
                ? `${record.name || ''} ${record.surname || ''}`.trim() 
                : 'İsim Belirtilmemiş'}
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {record.phone}
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Durum',
      dataIndex: 'verification_status',
      key: 'verification_status',
      render: (status) => (
        <Tag color={getVerificationColor(status)}>
          {status === 'verified' && 'Doğrulandı'}
          {status === 'pending' && 'Beklemede'}
          {status === 'failed' && 'Başarısız'}
        </Tag>
      ),
    },
    {
      title: 'Kayıt Tarihi',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => (
        <div>
          <div>{new Date(date).toLocaleDateString('tr-TR')}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {new Date(date).toLocaleTimeString('tr-TR')}
          </div>
        </div>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={2} style={{ color: '#1890ff', fontWeight: 600 }}>
          🧠 GMH-AI Kontrolünde Operasyon Merkezi
        </Title>
                <Space>
          <Button
            type="default"
            icon={<SettingOutlined />}
            onClick={() => setCustomizerVisible(true)}
          >
            Layout Özelleştir
          </Button>
          <Button
            type={dbConnected === true ? "default" : dbConnected === false ? "danger" : "primary"}
            icon={<DatabaseOutlined />}
            onClick={testRailwayConnection}
            style={{
              borderColor: dbConnected === true ? '#52c41a' : dbConnected === false ? '#ff4d4f' : undefined,
              color: dbConnected === true ? '#52c41a' : dbConnected === false ? '#ff4d4f' : undefined
            }}
          >
            {dbConnected === true ? 'Railway Bağlı ✅' : dbConnected === false ? 'Railway Bağlantısız ❌' : 'Railway Test Et'}
          </Button>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={fetchDashboardData}
            loading={loading}
          >
            Yenile
          </Button>
        </Space>
      </div>

      {/* AI SYSTEMS - ANA ALAN (Büyük) */}
      <div style={{ marginBottom: 32 }}>
        
        {/* AI Intelligence Overview - Responsive */}
        {/* AI Intelligence Overview - With Layout Control */}
        {isWidgetVisible('ai-stats') && (
          <Row gutter={[24, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              <Card style={{ textAlign: 'center', background: '#f6ffed', borderColor: '#52c41a', minHeight: '120px' }}>
                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#52c41a' }}>
                  🤖 AI Zekası
                </div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a', marginTop: 8 }}>
                  78.5%
                </div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                  Sürekli Öğreniyor
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card style={{ textAlign: 'center', background: '#fff2e8', borderColor: '#fa8c16', minHeight: '120px' }}>
                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#fa8c16' }}>
                  🧠 Kalıp Tanıma
                </div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#fa8c16', marginTop: 8 }}>
                  1,247/dk
                </div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                  İşlem Hızı
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card style={{ textAlign: 'center', background: '#f0f9ff', borderColor: '#1890ff', minHeight: '120px' }}>
                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>
                  🎯 Otomatik Tespit
                </div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff', marginTop: 8 }}>
                  23 Aktif
                </div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                  Gerçek Zamanlı
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card style={{ textAlign: 'center', background: '#f9f0ff', borderColor: '#722ed1', minHeight: '120px' }}>
                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#722ed1' }}>
                  📈 Öğrenme Oranı
                </div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#722ed1', marginTop: 8 }}>
                  +0.3%
                </div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                  Son Saat İyileşme
                </div>
              </Card>
            </Col>
          </Row>
        )}

        {/* REAL-TIME AI MONITORING - With Layout Control */}
        <Row gutter={[24, 16]} style={{ marginBottom: 32 }}>
          {isWidgetVisible('ai-realtime') && (
            <Col {...getWidgetSize('ai-realtime')} xs={24} lg={isWidgetVisible('ai-learning') ? 15 : 24}>
              <AIRealTimeMonitor />
            </Col>
          )}
          {isWidgetVisible('ai-learning') && (
            <Col {...getWidgetSize('ai-learning')} xs={24} lg={isWidgetVisible('ai-realtime') ? 9 : 24}>
              <AILearningProgress />
            </Col>
          )}
          {isWidgetVisible('safety-intelligence') && (
            <Col {...getWidgetSize('safety-intelligence')} xs={24} lg={15}>
              <SafetyIntelligenceWidget />
            </Col>
          )}
        </Row>
      </div>

      {/* Sistem Özeti - With Layout Control */}
      {isWidgetVisible('system-stats') && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card style={{ textAlign: 'center', minHeight: '120px' }}>
              <Statistic
                title="Toplam Kullanıcı"
                value={stats.totalUsers}
                prefix={<UserOutlined />}
                valueStyle={{ color: '#1890ff', fontSize: '24px' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card style={{ textAlign: 'center', minHeight: '120px' }}>
              <Statistic
                title="Aktif Kullanıcı"
                value={stats.activeUsers}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: '#3f8600', fontSize: '24px' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card style={{ textAlign: 'center', minHeight: '120px' }}>
              <Statistic
                title="Bugün Kayıt"
                value={stats.todayRegistrations}
                prefix={<RiseOutlined />}
                valueStyle={{ color: '#722ed1', fontSize: '24px' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card style={{ textAlign: 'center', minHeight: '120px' }}>
              <Statistic
                title="OTP (1h)"
                value={stats.recentOtpRequests}
                prefix={<PhoneOutlined />}
                valueStyle={{ color: '#fa541c', fontSize: '24px' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Doğrulama İstatistikleri */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <Card title="🔍 Kimlik Doğrulama Durumu">
            <Row gutter={16}>
              <Col span={12}>
                <Statistic
                  title="Bekleyen Doğrulama"
                  value={stats.pendingVerifications}
                  prefix={<ExclamationCircleOutlined />}
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Doğrulama Oranı"
                  value={stats.verificationRate}
                  suffix="%"
                  prefix={<IdcardOutlined />}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Col>
            </Row>
            <div style={{ marginTop: 16 }}>
              <Progress 
                percent={parseFloat(stats.verificationRate)} 
                status={parseFloat(stats.verificationRate) > 80 ? 'success' : 'active'}
                strokeColor={{
                  '0%': '#108ee9',
                  '100%': '#87d068',
                }}
              />
            </div>
          </Card>
        </Col>
        <Col span={12}>
          <Card title="👥 Kullanıcı Aktivitesi">
            <Row gutter={16}>
              <Col span={12}>
                <Statistic
                  title="Aktiflik Oranı"
                  value={stats.activeRate}
                  suffix="%"
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Pasif Kullanıcı"
                  value={stats.totalUsers - stats.activeUsers}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ color: '#cf1322' }}
                />
              </Col>
            </Row>
            <div style={{ marginTop: 16 }}>
              <Progress 
                percent={parseFloat(stats.activeRate)} 
                status={parseFloat(stats.activeRate) > 70 ? 'success' : 'active'}
                strokeColor={{
                  '0%': '#ff7875',
                  '100%': '#52c41a',
                }}
              />
            </div>
          </Card>
        </Col>
      </Row>

      {/* Son Kayıt Olan Kullanıcılar */}
      <Card title="🆕 Son Kayıt Olan Kullanıcılar" style={{ marginBottom: 24 }}>
        <Table
          columns={recentUsersColumns}
          dataSource={recentUsers}
          rowKey="id"
          loading={loading}
          pagination={false}
          size="small"
        />
      </Card>

      {/* Sistem Durumu */}
      <Row gutter={16}>
        <Col span={8}>
          <Card title="🔄 Sistem Durumu">
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Backend API</span>
                <Tag color="green">Çalışıyor</Tag>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Railway Database</span>
                <Tag color="green">Bağlı</Tag>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>OTP Servisi</span>
                <Tag color="green">Aktif</Tag>
              </div>
            </Space>
          </Card>
        </Col>
        <Col span={8}>
          <Card title="📈 Performans">
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <div>API Yanıt Süresi</div>
                <Progress percent={85} size="small" status="success" />
                <span style={{ fontSize: '12px', color: '#666' }}>~120ms</span>
              </div>
              <div>
                <div>Database Bağlantısı</div>
                <Progress percent={92} size="small" status="success" />
                <span style={{ fontSize: '12px', color: '#666' }}>~45ms</span>
              </div>
            </Space>
          </Card>
        </Col>
        <Col span={8}>
          <Card title="⚡ Hızlı İşlemler">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button type="link" href="/users" style={{ padding: 0, height: 'auto' }}>
                👥 Tüm Kullanıcıları Görüntüle
              </Button>
              <Button type="link" href="/otp" style={{ padding: 0, height: 'auto' }}>
                🔐 OTP İsteklerini İncele
              </Button>
              <Button type="link" href="/emergency" style={{ padding: 0, height: 'auto' }}>
                🚨 Acil Durumları Kontrol Et
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>


      {/* Layout Customizer */}
      <LayoutCustomizer
        visible={customizerVisible}
        onClose={() => setCustomizerVisible(false)}
        onLayoutChange={handleLayoutChange}
        currentLayout={currentLayout}
      />
    </div>
  );
};

export default DashboardPage;